import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services';
import Logo from '../../components/common/Logo';
import { useAuth } from '../../context/AuthContext';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [countryCode, setCountryCode] = useState('+977');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullPhone, setFullPhone] = useState('+977');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // keep mount-only hook for symmetry; no OTP flow now
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!fullName.trim()) errors.fullName = t('form.validation.required');
    if (!email.trim()) errors.email = t('form.validation.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t('form.validation.invalidEmail');
    if (!countryCode || !/^\+\d{1,4}$/.test(countryCode)) errors.countryCode = t('form.validation.required');
    if (!phoneNumber || phoneNumber.length < 7) errors.phoneNumber = t('form.validation.required');
    if (!password) {
      errors.password = t('form.validation.required');
    } else if (password.length < 8) {
      errors.password = t('form.validation.minLength', { min: 8 });
    }
    if (password !== confirmPassword) errors.confirmPassword = t('form.validation.passwordMismatch');
    if (!acceptedTerms) errors.terms = t('form.validation.termsRequired');

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        password,
        countryCode,
        phoneNumber: phoneNumber.trim(),
      };

      const response = await authService.register(payload);

      if (response?.data?.accessToken) {
        const userData = {
          email: email.trim().toLowerCase(),
          role: response.data.role || 'DONOR',
          fullName: fullName.trim(),
        };
        authLogin(response.data.accessToken, userData, response.data.refreshToken);
        navigate('/');
      } else {
        navigate('/login', {
          state: { message: t('auth.registerSuccessMessage') }
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block transition transform hover:scale-105">
              <Logo size="lg" showText={false} />
            </Link>
            <h2 className="mt-4 text-3xl font-extrabold text-gray-900 tracking-tight">
              {t('auth.registerTitle')}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.registerSubtitleWelcome')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex items-center gap-3 animate-shake">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 animate-fadeIn">
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.fullName')} <span className="text-red-500">*</span>
        </label>
        <input
          id="fullName"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          placeholder={t('auth.fullNamePlaceholder')}
          required
        />
        {fieldErrors.fullName && <p className="mt-1 text-xs text-red-600">{fieldErrors.fullName}</p>}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.email')} <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          placeholder={t('auth.emailPlaceholder')}
          required
        />
        {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
      </div>

      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.phoneNumber')} <span className="text-red-500">*</span>
        </label>
        <PhoneInput
          defaultCountry="np"
          value={fullPhone}
          onChange={(phone, meta) => {
            setFullPhone(phone);
            const dial = meta?.country?.dialCode ? `+${meta.country.dialCode}` : '';
            setCountryCode(dial || countryCode);
            const digitsOnly = String(phone || '').replace(/\D/g, '');
            const dialDigits = String(meta?.country?.dialCode || '');
            const national = dialDigits && digitsOnly.startsWith(dialDigits)
              ? digitsOnly.slice(dialDigits.length)
              : digitsOnly;
            setPhoneNumber(national);
          }}
          inputClassName={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.phoneNumber || fieldErrors.countryCode ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
        />
        {(fieldErrors.countryCode || fieldErrors.phoneNumber) && (
          <p className="mt-1 text-xs text-red-600">
            {fieldErrors.phoneNumber || fieldErrors.countryCode}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.password')} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            placeholder={t('auth.passwordPlaceholder')}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
          >
            {showPassword ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
            )}
          </button>
        </div>
        {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
          {t('auth.confirmPassword')} <span className="text-red-500">*</span>
        </label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${fieldErrors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          placeholder={t('auth.confirmPasswordPlaceholder')}
          required
        />
        {fieldErrors.confirmPassword && <p className="mt-1 text-xs text-red-600">{fieldErrors.confirmPassword}</p>}
      </div>

      <div className="flex items-start">
        <input
          id="terms"
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
          {t('auth.iAgreeTo')} <button type="button" onClick={() => setShowTermsModal(true)} className="text-primary-600 hover:text-primary-700 underline font-medium font-nepali">
            {t('donate.modal.termsLink')}
          </button>
        </label>
      </div>
      {fieldErrors.terms && <p className="mt-1 text-xs text-red-600">{fieldErrors.terms}</p>}

      <button
        type="submit"
        disabled={loading || !acceptedTerms}
        className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 shadow-md transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
      </button>
    </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-600">
              {t('auth.alreadyHaveAccount')} <Link to="/login" className="text-primary-600 font-bold hover:text-primary-700 transition">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white p-8 rounded-2xl max-w-lg w-full shadow-2xl animate-scaleUp">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('legal.terms.title')}
            </h3>
            <div className="max-h-72 overflow-y-auto text-sm text-gray-700 space-y-4 pr-2 bg-gray-50 p-4 rounded-xl border border-gray-100">
              <p className="text-xs text-gray-500">{t('legal.terms.meta.lastUpdated')}</p>
              <p className="text-gray-700 leading-relaxed">{t('legal.terms.meta.intro')}</p>

              <div className="space-y-4">
                {(t('legal.terms.sections', { returnObjects: true }) || []).map((section, idx) => (
                  <section key={idx} className="space-y-2">
                    <h4 className="font-semibold text-gray-900">{section.title}</h4>

                    {Array.isArray(section.paragraphs) && section.paragraphs.length > 0 && (
                      <div className="space-y-2">
                        {section.paragraphs.map((p, pIdx) => (
                          <p key={pIdx} className="text-gray-700 leading-relaxed">
                            {p}
                          </p>
                        ))}
                      </div>
                    )}

                    {Array.isArray(section.bullets) && section.bullets.length > 0 && (
                      <ul className="list-disc ml-5 space-y-1">
                        {section.bullets.map((b, bIdx) => (
                          <li key={bIdx}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>

              <div className="pt-3 border-t text-xs text-gray-600 space-y-1">
                <p className="font-semibold text-gray-700">{t('legal.terms.agreement.title')}</p>
                <p>{t('legal.terms.agreement.text')}</p>
              </div>
            </div>
            <button
              onClick={() => setShowTermsModal(false)}
              className="mt-8 w-full py-3 bg-primary-600 text-white rounded-lg font-bold hover:bg-primary-700 shadow-lg transform hover:-translate-y-0.5 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
